
import { defineComponent, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import PageHeader from '@/components/layout/PageHeader.vue';
import { useRouter } from 'vue-router';
import { findPermission } from '@/composables/User';
import { NestApiType, nestCall } from '@/composables/nestApi';
import { showToastError, showToastSuccess } from '@/helpers';
import ListCreatePanelV2 from '@/components/mes-lists/ListCreatePanelV2.vue';
import ListSegmentationEditor from '@/components/mes-lists/ListSegmentationEditor.vue';
import ImportModal from '@/components/import/ImportModal.vue';
import SpmTableAPI from '@/components/table-rest-api/SpmTableAPI.vue';
import SpmBadge from '@/components/table-rest-api/SpmBadge.vue';
import SpmDateDisplay from '@/components/table-rest-api/SpmDateDisplay.vue';
import { formatInteger } from '@/components/table-rest-api/formatters';
import type {
  SpmColumnConfig,
  ApiCallConfig,
  SpmApiFilter,
  SpmActiveSort,
} from '@/components/table-rest-api/types';
import { SpmFilterOperator } from '@/components/table-rest-api/filters';
import IntegrationOwnerBadge from '@/components/integrations/IntegrationOwnerBadge.vue';
import RenameListAction from './ListsManageActions/RenameListAction.vue';
import DuplicateListAction from './ListsManageActions/DuplicateListAction.vue';
import DeleteListAction from './ListsManageActions/DeleteListAction.vue';
import useListsActions from './ListsManageActions/useListsActions';
import type {
  RenameListResult,
  DuplicateListResult,
  DeleteListResult,
} from './ListsManageActions/types';

export default defineComponent({
  name: 'ListsManageV2',
  components: {
    PageHeader,
    SpmTableAPI,
    SpmBadge,
    SpmDateDisplay,
    IntegrationOwnerBadge,
    ListCreatePanelV2,
    RenameListAction,
    DuplicateListAction,
    DeleteListAction,
    ListSegmentationEditor,
    ImportModal,
  },

  setup() {
    const { t } = useI18n();
    const router = useRouter();
    const childComponentRef = ref();

    // Action component refs
    const renameListRef = ref(null);
    const duplicateListRef = ref(null);
    const deleteListRef = ref(null);

    // Modal states
    const showSegmentationEditor = ref(false);
    const showImportModal = ref(false);
    const selectedListId = ref<number>(0);

    /**
     * Reload table data after an action
     */
    const reloadTableData = () => {
      if (childComponentRef.value) {
        childComponentRef.value.reloadData();
      }
    };

    const setLoading = async (loading: boolean) => {
      if (childComponentRef.value) {
        await childComponentRef.value.setLoading(loading);
      }
    };

    // Handlers for opening modals
    const handleOpenImportModal = (listId: number) => {
      selectedListId.value = listId;
      showImportModal.value = true;
    };

    const handleOpenSegmentationEditor = (listId: number) => {
      selectedListId.value = listId;
      showSegmentationEditor.value = true;
    };

    const onSegmentationSaved = () => {
      showSegmentationEditor.value = false;
      router.push(`/lists/manage-contacts/${selectedListId.value}`);
    };

    // Use the composable for row actions
    const { rowActions } = useListsActions({
      renameListRef,
      duplicateListRef,
      deleteListRef,
      onOpenImportModal: handleOpenImportModal,
      onOpenSegmentationEditor: handleOpenSegmentationEditor,
    });

    // Action confirm handlers
    const onRenameConfirm = async (result: RenameListResult) => {
      try {
        setLoading(true);
        const response = await nestCall(
          'patch',
          NestApiType.CLIENT_API,
          `/lists/${result.listId}`,
          {},
          '',
          { name: result.newName },
          true,
        );
        if (response && response.id_shop_list) {
          showToastSuccess(t('myLists.manage.actions.listRename.success'));
          reloadTableData();
        } else {
          showToastError(t('myLists.manage.actions.listRename.error'));
        }
      } catch (error) {
        console.error('Rename list API error:', error);
        showToastError(t('myLists.manage.actions.listRename.error'));
      } finally {
        setLoading(false);
      }
    };

    const onDuplicateConfirm = async (result: DuplicateListResult) => {
      try {
        setLoading(true);
        const payload = {
          action_type: 'duplicate',
          selection_type: 'all',
          selected_ids: [],
          filters: [],
          source_list_id: result.listId,
          new_list_name: result.newName,
        };

        const response = await nestCall(
          'post',
          NestApiType.CLIENT_API,
          '/list-actions',
          {},
          '',
          payload,
          true,
        );

        if (response && response.success) {
          if (response.message === 'request_pending') {
            showToastSuccess(t('myLists.manage.actions.listDuplicate.queued'));
          } else {
            showToastSuccess(t('myLists.manage.actions.listDuplicate.success'));
          }
          reloadTableData();
        } else {
          showToastError(t('myLists.manage.actions.listDuplicate.error'));
        }
      } catch (error) {
        console.error('Duplicate list API error:', error);
        showToastError(t('myLists.manage.actions.listDuplicate.error'));
      } finally {
        setLoading(false);
      }
    };

    const onDeleteConfirm = async (result: DeleteListResult) => {
      try {
        setLoading(true);
        const response = await nestCall(
          'delete',
          NestApiType.CLIENT_API,
          `/lists/${result.listId}`,
          {},
          '',
          {},
          true,
        );

        if (response && response.statusCode === 200) {
          showToastSuccess(t('myLists.manage.actions.listDelete.success'));
          reloadTableData();
        } else {
          showToastError(t('myLists.manage.actions.listDelete.error'));
        }
      } catch (error) {
        console.error('Delete list API error:', error);
        showToastError(t('myLists.manage.actions.listDelete.error'));
      } finally {
        setLoading(false);
      }
    };

    // API call configuration
    const apiCall = ref<ApiCallConfig | null>({
      type_api: NestApiType.CLIENT_API,
      path: '/lists',
      secure: true,
    });

    // Persistent filters - only fetch valid and active lists
    const persistentFilters: SpmApiFilter[] = [
      {
        field: 'valid',
        operator: SpmFilterOperator.EQUALS,
        values: 1,
      },
      {
        field: 'active',
        operator: SpmFilterOperator.EQUALS,
        values: 1,
      },
    ];

    // Initial sort - most recently created lists first
    const initialSort: SpmActiveSort = {
      field: 'date_add',
      order: 'desc',
    };

    // Columns configuration - only name, type, total_contacts (filterable and sortable)
    const columns: SpmColumnConfig[] = [
      {
        id: 'name',
        header: t('myLists.manage.headers.name'),
        maxWidth: '40%',
        fields: [
          {
            name: 'name',
            label: t('myLists.manage.headers.name'),
            sortable: true,
            filterable: true,
            filterConfig: { type: 'textInput' },
          },
          'id_shop_list',
          {
            name: 'date_add',
            label: t('myLists.manage.headers.dateAdd'),
            sortable: true,
            filterable: true,
            filterConfig: { type: 'dateRange' },
          },
        ],
      },
      {
        id: 'type',
        header: t('myLists.manage.headers.type'),
        fields: [
          {
            name: 'type',
            label: t('myLists.manage.headers.type'),
            sortable: true,
            filterable: true,
            filterConfig: {
              type: 'multiSelect',
              options: [
                { label: t('myLists.manage.listTypes.list'), value: 'list' },
                { label: t('myLists.manage.listTypes.segment'), value: 'segment' },
              ],
            },
          },
        ],
      },
      {
        id: 'total_contacts',
        header: t('myLists.manage.headers.totalContacts'),
        fields: [
          {
            name: 'total_contacts',
            label: t('myLists.manage.headers.totalContacts'),
            sortable: true,
            filterable: true,
            filterConfig: { type: 'numberInput' },
          },
        ],
      },
    ];

    const getTypeBadgeIcons = (type: string | null | undefined) => {
      if (type === 'segment') {
        return 'fa fa-filter';
      }
      return 'fa fa-list';
    };

    // Helper function for type badge colors
    const getTypeBadgeColors = (type: string | null | undefined) => {
      if (type === 'segment') {
        return { bg: 'var(--purple-100)', color: 'var(--purple-700)' };
      }
      return { bg: 'var(--blue-100)', color: 'var(--blue-700)' };
    };

    // Format contacts count using shared formatter
    const formatContactsCount = (value: number | null | undefined): string => formatInteger(value);

    return {
      t,
      columns,
      childComponentRef,
      findPermission,
      apiCall,
      rowActions,
      persistentFilters,
      initialSort,
      getTypeBadgeColors,
      getTypeBadgeIcons,
      formatContactsCount,
      // Action refs
      renameListRef,
      duplicateListRef,
      deleteListRef,
      // Action handlers
      onRenameConfirm,
      onDuplicateConfirm,
      onDeleteConfirm,
      // Modal states
      showSegmentationEditor,
      showImportModal,
      selectedListId,
      onSegmentationSaved,
    };
  },
});
